// client/src/features/documents/mine.ts — „Documentele mele" (SCR-DOC-001 since D-2570).
//
// ONE READ, SHAPED HERE. `GET /api/v1/documents/mine` answers every document that concerns the
// signed-in owner, whole and unpaged, each already placed on an apartment (or on the owner
// personally), on a shelf, and with the facts of the record that put it there (D-2571). This file
// groups, filters and words it; it decides nothing the server did not. The grouping is by the
// server's `propertyId` and `category`, the filter is a string match over what the row already
// says, and the „de ce e aici" sentence is built from the row's own names and dates.
//
// THE SENTENCE IS THE CLIENT'S, the facts are the server's — the arrangement every other enum on
// the wire has: the server sends `PAYMENT_PROOF` and „CTR-2025-0101", and the Romanian around them
// lives in `romanian.json` beside the rest of the copy (04 §9.8).

import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import type {
  OwnerDocumentAuthor,
  OwnerDocumentCategory,
  OwnerDocumentItem,
  OwnerDocumentProperty,
  OwnerDocumentsView,
} from '@/client';
import { listMyDocumentsOptions } from '@/client/@tanstack/react-query.gen';
import type { IconName } from '@/components/icons';
import { fiscalVisible } from '@/config/fiscal';
import { invoicesVisible } from '@/config/invoices';
import { utilityPaymentsVisible } from '@/config/utility-payments';
import { formatDate, formatMonthYear } from '@/lib/format';
import { fold } from '@/lib/text';

import { isWithdrawnKind } from './enums';

/**
 * Each shelf's glyph, in the order the shelves are read: the apartment's own papers first, then
 * the lease and what it produces, then running the flat, then money and the state. The record is
 * typed over the whole union, so a shelf the server adds is a type error here until it has one.
 */
export const CATEGORY_ICON: Record<OwnerDocumentCategory, IconName> = {
  PROPERTY: 'building',
  CONTRACTS: 'document-text',
  PAYMENTS: 'banknotes',
  UTILITIES: 'bolt',
  REPAIRS: 'wrench',
  INSURANCE: 'shield-check',
  EXPENSES: 'receipt',
  FISCAL: 'scale',
  PERSONAL: 'identification',
};

/** The shelves as a tuple, for the URL schema; {@link CATEGORY_ICON} is what keeps it whole. */
export const OWNER_DOCUMENT_CATEGORIES = [
  'PROPERTY',
  'CONTRACTS',
  'PAYMENTS',
  'UTILITIES',
  'REPAIRS',
  'INSURANCE',
  'EXPENSES',
  'FISCAL',
  'PERSONAL',
] as const satisfies readonly OwnerDocumentCategory[];

/** Who filed a document, in the order the filter offers them. */
export const OWNER_DOCUMENT_AUTHORS = [
  'YOU',
  'TENANT',
  'LINK',
  'MEMBER',
  'SYSTEM',
] as const satisfies readonly OwnerDocumentAuthor[];

/** The URL's name for the owner's own papers, which hang off no apartment. */
export const PERSONAL = 'personal';

/**
 * `GET /api/v1/documents/mine`, whole or narrowed to one apartment. The generated options, so
 * the key is the generated one and a vault write invalidates it with the archive (`queries.ts`).
 *
 * Read through {@link withoutWithdrawn}: the one read is unpaged, so dropping a row here drops
 * it from every list, shelf and count the hub and the apartment's „Documente" tab draw.
 */
export function useMyDocuments(propertyId?: string) {
  return useQuery({
    ...listMyDocumentsOptions(propertyId === undefined ? undefined : { query: { propertyId } }),
    select: withoutWithdrawn,
  });
}

/**
 * The archive less what belongs to a module withdrawn from view (D-2780): the fiscal shelf —
 * declarations, their recipise, the annual dossiers — while the fiscal module is, the scanned
 * supplier invoices while the invoices are, and the proofs of a utility bill paid while the
 * utility payments are (D-2700). The rows stay the server's; the screens do not read them.
 */
function withoutWithdrawn(view: OwnerDocumentsView): OwnerDocumentsView {
  return { ...view, items: view.items.filter((item) => !withdrawn(item)) };
}

function withdrawn(item: OwnerDocumentItem): boolean {
  return (
    isWithdrawnKind(item.kind) ||
    (item.category === 'FISCAL' && !fiscalVisible()) ||
    (item.origin === 'INVOICE_SCAN' && !invoicesVisible()) ||
    (item.origin === 'UTILITY_BILL_PROOF' && !utilityPaymentsVisible())
  );
}

/** The shelves a person is offered — in a filter, or named as empty: all of them, less a withdrawn one. */
export function offeredCategories(): OwnerDocumentCategory[] {
  return OWNER_DOCUMENT_CATEGORIES.filter((category) => category !== 'FISCAL' || fiscalVisible());
}

/**
 * A shelf's name. „Cheltuieli și facturi" is „Cheltuieli" while the supplier invoices are
 * withdrawn from view (D-2780) — the shelf then holds the receipts alone.
 */
export function categoryLabel(category: OwnerDocumentCategory, t: TFunction): string {
  return category === 'EXPENSES' && !invoicesVisible()
    ? t('documents.mine.categoryExpensesWithoutInvoices')
    : t(`enums.OwnerDocumentCategory.${category}`);
}

/** The key a row is grouped under: an apartment's id, or {@link PERSONAL}. */
export function placeOf(item: OwnerDocumentItem): string {
  return item.propertyId ?? PERSONAL;
}

/** The rows of one place, per shelf, shelves in reading order and empty ones left out. */
export function shelves(
  items: readonly OwnerDocumentItem[],
): { category: OwnerDocumentCategory; items: OwnerDocumentItem[] }[] {
  return OWNER_DOCUMENT_CATEGORIES.map((category) => ({
    category,
    items: items.filter((item) => item.category === category),
  })).filter((shelf) => shelf.items.length > 0);
}

/**
 * One line of a list: a document, or the four leasing PDFs of one lease folded into one line.
 * Unfolded, a landlord with eighteen leases reads seventy-two generated rows before their own
 * papers; folded, each lease is one line with four downloads on it.
 */
export type DocumentEntry =
  | { type: 'document'; key: string; item: OwnerDocumentItem }
  | { type: 'generated'; key: string; items: OwnerDocumentItem[] };

/** The lines of a list, generated PDFs folded per lease, in the order the rows arrived. */
export function entriesOf(items: readonly OwnerDocumentItem[]): DocumentEntry[] {
  const entries: DocumentEntry[] = [];
  const bundles = new Map<string, OwnerDocumentItem[]>();

  for (const item of items) {
    if (item.source === 'GENERATED' && item.contractId != null) {
      const bundle = bundles.get(item.contractId);
      if (bundle !== undefined) {
        bundle.push(item);
        continue;
      }
      const fresh = [item];
      bundles.set(item.contractId, fresh);
      entries.push({ type: 'generated', key: `generated:${item.contractId}`, items: fresh });
      continue;
    }
    entries.push({ type: 'document', key: item.key, item });
  }

  return entries;
}

/** How many rows each place holds — the counts beside the apartments. */
export function countByPlace(items: readonly OwnerDocumentItem[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items) counts.set(placeOf(item), (counts.get(placeOf(item)) ?? 0) + 1);
  return counts;
}

/** The apartment a row sits on, by id, for the „Toate" view's column. */
export function propertyNames(properties: readonly OwnerDocumentProperty[]): Map<string, string> {
  return new Map(properties.map((property) => [property.id, property.name]));
}

/** `YYYY-MM` → „martie 2026", `YYYY` → „2025". */
export function formatPeriod(period: string | null | undefined): string {
  if (period == null || period === '') return '';
  return period.length === 4 ? period : formatMonthYear(period);
}

/**
 * Why a document is here, as one Romanian sentence. Every origin has a sentence that needs none
 * of the optional facts, so a row whose record has lost a name still reads as a sentence and not
 * as „Atașat contractului ." — the `…Bare` keys are those fallbacks. Every key is a literal
 * (04 §9.6.3: only `enums.*` may be computed), which is why this is a switch and not a template.
 */
export function reasonOf(item: OwnerDocumentItem, t: TFunction): string {
  const label = item.originLabel ?? '';
  const detail = item.originDetail ?? '';
  const date = item.originDate == null ? '' : formatDate(item.originDate);
  const period = formatPeriod(item.originPeriod);

  switch (item.origin) {
    case 'PROPERTY_FILE':
      return t('documents.mine.reason.propertyFile');
    case 'UNIT_FILE':
      return label === ''
        ? t('documents.mine.reason.unitFileBare')
        : t('documents.mine.reason.unitFile', { label });
    case 'CONTRACT_ATTACHMENT':
      return detail === ''
        ? t('documents.mine.reason.contractAttachmentBare', { label })
        : t('documents.mine.reason.contractAttachment', { label, detail });
    case 'CONTRACT_GENERATED':
      return detail === ''
        ? t('documents.mine.reason.contractGeneratedBare', { label })
        : t('documents.mine.reason.contractGenerated', { label, detail });
    case 'TENANT_FILE':
      return detail === ''
        ? t('documents.mine.reason.tenantFileBare', { label })
        : t('documents.mine.reason.tenantFile', { label, detail });
    case 'PAYMENT_PROOF':
      return period === ''
        ? t('documents.mine.reason.paymentProofBare', { label, date })
        : t('documents.mine.reason.paymentProof', { label, period });
    case 'UTILITY_BILL_PROOF':
      return period === ''
        ? t('documents.mine.reason.utilityBillProofBare', { label })
        : t('documents.mine.reason.utilityBillProof', { label, period });
    case 'METER_READING_PHOTO':
      return t('documents.mine.reason.meterReadingPhoto', { label, date });
    case 'REPAIR_PHOTO':
      return t('documents.mine.reason.repairPhoto', { label, date });
    case 'MAINTENANCE_REPORT':
      return t('documents.mine.reason.maintenanceReport', { label, date });
    case 'EQUIPMENT_FILE':
      return label === ''
        ? t('documents.mine.reason.equipmentFileBare')
        : t('documents.mine.reason.equipmentFile', { label });
    case 'INSURANCE_POLICY':
      return t('documents.mine.reason.insurancePolicy', { label, detail, date });
    case 'EXPENSE_RECEIPT':
      return t('documents.mine.reason.expenseReceipt', { label, date });
    case 'INVOICE_SCAN': {
      const supplier = label === '' ? t('documents.mine.reason.unknownSupplier') : label;
      return item.originId == null
        ? t('documents.mine.reason.invoiceScanPending', { label: supplier })
        : t('documents.mine.reason.invoiceScan', { label: supplier });
    }
    case 'DECLARATION_ARTIFACT':
      return item.kind === 'RECIPISA'
        ? t('documents.mine.reason.declarationRecipisa', { label, period })
        : t('documents.mine.reason.declarationArtifact', { label, period });
    case 'FISCAL_DOSSIER':
      return t('documents.mine.reason.fiscalDossier', { period });
    case 'OWNER_FILE':
      return t('documents.mine.reason.ownerFile');
    case 'OWNER_TENANCY_FILE':
      return detail === ''
        ? t('documents.mine.reason.ownerTenancyFileBare', { label })
        : t('documents.mine.reason.ownerTenancyFile', { label, detail });
  }
}

/** Who put it there, as the row's second line says it. */
export function authorOf(item: OwnerDocumentItem, t: TFunction): string {
  const name = item.authorName ?? '';

  switch (item.author) {
    case 'YOU':
      return t('documents.mine.author.you');
    case 'MEMBER':
      return name === ''
        ? t('documents.mine.author.memberBare')
        : t('documents.mine.author.member', { name });
    case 'TENANT':
      return name === ''
        ? t('documents.mine.author.tenantBare')
        : t('documents.mine.author.tenant', { name });
    case 'LINK':
      return t('documents.mine.author.link');
    case 'SYSTEM':
      return t('documents.mine.author.system');
  }
}

/**
 * Whether a row matches the „Toate" view's search box: the title, the file name, the apartment
 * and every fact of the reason, diacritics folded — „chitanta" finds „Chitanță".
 */
export function matchesText(
  item: OwnerDocumentItem,
  needle: string,
  placeName: string,
  reason: string,
): boolean {
  const folded = fold(needle.trim());
  if (folded === '') return true;

  return [item.title, item.fileName, placeName, reason, item.authorName]
    .filter((part): part is string => typeof part === 'string' && part !== '')
    .some((part) => fold(part).includes(folded));
}
